import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { notifyIcon } from "../../config";
import { getUserProfileControlsData } from "../../shared/services/auth.service";
import { getNotification } from "../../utils/common";
import { ExpressionBuilderModal, useExpressionBuilder } from "../Workflow/ExpressionBuilder";
import { useWorkflowContext } from "../Workflow/WorkflowContextProvider";
import { useFormChipHelper } from "../Workflow/WorkflowVariables/hooks/workflowVariables.hooks";

/**
 * Expression builder dialog for form dropdown “Show If” visibility rules (DropdownConfiguration).
 * Persists the rule tree via {@link useFormChipHelper.updateFormChipProperty} as `showIfExpression`.
 *
 * @param {Object} props
 * @param {Function} props.onClose - Called after successful save or when closing from header (parent handles Dialog unmount).
 * @returns {JSX.Element}
 * @author Abhishek Dhillon
 * @parent DropdownShowIfSection
 */
const ShowIfPopup = ({ onClose, selectedElement, selectedSubsection, handleSubmit }) => {
  const { customWorkflowVariables, workflowDefaults } = useWorkflowContext();
  const { user } = useSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(true);
  const [userChips, setUserChips] = useState([]);

  const nodeExpressionForBuilder = useMemo(() => {
    const raw = selectedElement?.showIfExpression ?? selectedSubsection?.showIfExpression;
    if (raw == null) {
      return null;
    }
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return raw;
  }, [selectedElement?.showIfExpression, selectedSubsection?.showIfExpression]);

  /**
   * Loads user chip catalog used by the field selector inside the rule builder.
   * @author Abhishek Dhillon
   */
  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const userChipsResponse = await getUserProfileControlsData({ companyId: user?.companyId, drutasUserId: user?.id });
      if (userChipsResponse) {
        setUserChips(userChipsResponse?.WorkInformations);
      }
    } catch {
      getNotification("Failed to load data", notifyIcon.ERROR_ICON);
    } finally {
      setIsLoading(false);
    }
  }, [user?.companyId, user?.id]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  /**
   * Save callback wired into useExpressionBuilder so the form-chip endpoint is used
   * instead of the workflow node expression endpoints.
   * @author Abhishek Dhillon
   */
  const customSaveExpression = useCallback(
    async (expression) => {
      await handleSubmit(expression);
    },
    [handleSubmit]
  );

  const {
    expression,
    updateRootExpression,
    handleSaveExpression,
    handleTestExpression,
    testData,
    evaluationResult,
    availableFields,
    showIfFields,
    fieldValidators,
    isSubmitting,
    submitError,
  } = useExpressionBuilder(
    customWorkflowVariables,
    onClose,
    selectedElement?.id ?? selectedSubsection?.id ?? "",
    nodeExpressionForBuilder,
    userChips,
    workflowDefaults?.adminCustomVariables,
    { customSaveExpression }
  );

  if (isLoading) {
    return (
      <div className='expression-loading-container'>
        <div className='d-flex justify-content-center align-items-center p-4'>
          <div className='spinner-border text-primary' role='status'>
            <span className='sr-only'>Loading expression...</span>
          </div>
          <span className='ms-2'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <ExpressionBuilderModal
      onClose={onClose}
      modalTitle={"Show If Configuration"}
      expression={expression}
      updateRootExpression={updateRootExpression}
      handleSaveExpression={handleSaveExpression}
      handleTestExpression={handleTestExpression}
      availableFields={showIfFields}
      testData={testData}
      evaluationResult={evaluationResult}
      fieldValidators={fieldValidators}
      isSubmitting={isSubmitting}
      submitError={submitError}
    />
  );
};

export default ShowIfPopup;
